import React from 'react';
import ArrowLeftGray from '../../img/arrow-left-gray.svg';
import ArrowLeftOrange from '../../img/arrow-left-orange.svg';
import ArrowRightGray from '../../img/arrow-right-gray.svg';
import ArrowRightOrange from '../../img/arrow-right-orange.svg';

/**
 * Paginação numerada do padrão do sistema (at-73).
 *
 * O bloco existia **copiado** em `recepcao.tsx` e `usersTable.tsx` — cerca de 150 linhas
 * cada, com a mesma lógica de reticências e o mesmo markup. Ao precisar dele numa
 * terceira tela (a aba Fornecedores da configuração de Cotações), foi extraído em vez de
 * copiado de novo.
 *
 * ⚠️ O estilo vem de `views/recepcao/pages/Recepcao.css` (classes `PaginaO`,
 * `page-number`, `Ellipse14`, `Anterior`/`PrXima`), que é onde o design da tabela do
 * sistema vive. **A tela que usar este componente precisa importar aquele CSS** — é o
 * mesmo acoplamento que as telas de tabela já têm hoje.
 *
 * ✅ at-82 (24/08/2026): `recepcao.tsx` e `usersTable.tsx` foram migradas — o bloco copiado
 * saiu das duas (121 e 55 linhas). O risco continua sendo **visual** (o markup é idêntico e
 * nenhum teste o afere), então a conferência das duas telas entra no roteiro manual.
 */

interface PaginacaoProps {
  paginaAtual: number;
  totalPaginas: number;
  onMudarPagina: (pagina: number) => void;
  /** Quantas páginas numeradas aparecem antes de virar reticências. */
  maximoVisivel?: number;
}

const Paginacao: React.FC<PaginacaoProps> = ({
  paginaAtual,
  totalPaginas,
  onMudarPagina,
  maximoVisivel = 10,
}) => {
  const numeros = (): React.ReactNode[] => {
    if (totalPaginas <= 1) {
      return [
        <div key={1} className="page-number active">
          1
          <div className="Ellipse14" />
        </div>,
      ];
    }

    const pages: React.ReactNode[] = [];

    pages.push(
      <div
        key={1}
        className={`page-number ${paginaAtual === 1 ? 'active' : ''}`}
        onClick={() => onMudarPagina(1)}
      >
        1
        {paginaAtual === 1 && <div className="Ellipse14" />}
      </div>,
    );

    if (paginaAtual > maximoVisivel - 4) {
      pages.push(
        <span key="left-ellipsis" className="ellipsis">
          ...
        </span>,
      );
    }

    const inicio = Math.max(
      2,
      Math.min(paginaAtual - Math.floor((maximoVisivel - 2) / 2), totalPaginas - (maximoVisivel - 2)),
    );
    const fim = Math.min(totalPaginas - 1, inicio + maximoVisivel - 3);

    for (let i = inicio; i <= fim; i++) {
      pages.push(
        <div
          key={i}
          className={`page-number ${paginaAtual === i ? 'active' : ''}`}
          onClick={() => onMudarPagina(i)}
        >
          {i}
          {paginaAtual === i && <div className="Ellipse14" />}
        </div>,
      );
    }

    if (fim < totalPaginas - 1) {
      pages.push(
        <span key="right-ellipsis" className="ellipsis">
          ...
        </span>,
      );
    }

    pages.push(
      <div
        key={totalPaginas}
        className={`page-number ${paginaAtual === totalPaginas ? 'active' : ''}`}
        onClick={() => onMudarPagina(totalPaginas)}
      >
        {totalPaginas}
        {paginaAtual === totalPaginas && <div className="Ellipse14" />}
      </div>,
    );

    return pages;
  };

  const naPrimeira = paginaAtual <= 1;
  const naUltima = paginaAtual >= totalPaginas;

  return (
    <div className="PaginaO">
      <div
        className={`Frame1000003950 ${naPrimeira ? 'disabled' : ''}`}
        onClick={() => {
          if (!naPrimeira) onMudarPagina(paginaAtual - 1);
        }}
        style={{ cursor: naPrimeira ? 'not-allowed' : 'pointer' }}
      >
        <img
          className="imgIcon"
          src={naPrimeira ? ArrowLeftGray : ArrowLeftOrange}
          alt="Seta para Esquerda"
          width="24"
          height="24"
        />
        <div className="Anterior" style={{ color: naPrimeira ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
          Anterior
        </div>
      </div>

      <div className="Group21">
        <div className="Group20">{numeros()}</div>
      </div>

      <div
        className={`Frame1000003949 ${naUltima ? 'disabled' : ''}`}
        onClick={() => {
          if (!naUltima) onMudarPagina(paginaAtual + 1);
        }}
        style={{ cursor: naUltima ? 'not-allowed' : 'pointer' }}
      >
        <div className="PrXima" style={{ color: naUltima ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
          Próxima
        </div>
        <img
          className="imgIcon"
          src={naUltima ? ArrowRightGray : ArrowRightOrange}
          alt="Seta para Direita"
          width="24"
          height="24"
        />
      </div>
    </div>
  );
};

export default Paginacao;
